import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Opportunity, OpportunityType } from "../types";
import { reportIssue } from "../services/parseService";

interface ReportIssueFormProps {
  isVisible: boolean;
  onClose: () => void;
  opportunity?: Opportunity;
}

type IssueType =
  | "Incorrect Dates"
  | "Invalid Information"
  | "Broken Link"
  | "Other";

const ReportIssueForm: React.FC<ReportIssueFormProps> = ({
  isVisible,
  onClose,
  opportunity,
}) => {
  // Helper function to get color for opportunity type
  const getTypeColor = (
    type?: OpportunityType | OpportunityType[] | string,
  ) => {
    if (!type) return "#3B82F6"; // Default blue

    // Handle array of types
    if (Array.isArray(type) && type.length > 0) {
      type = type[0];
    }

    switch (type) {
      case "Hackathon":
        return "#F43F5E"; // Rose
      case "Competition":
        return "#3B82F6"; // Blue
      case "Grant":
        return "#8B5CF6"; // Violet
      case "Scholarship":
        return "#10B981"; // Emerald
      case "Conference":
        return "#334155"; // Slate
      case "Workshop/Programs":
        return "#F59E0B"; // Amber
      case "Internship":
        return "#06B6D4"; // Cyan
      default:
        return "#71717A"; // Gray
    }
  };
  
  const [issueType, setIssueType] = useState<IssueType | null>(null);
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [scaleAnim] = useState(new Animated.Value(0));

  useEffect(() => {
    if (showSuccess) {
      Animated.spring(scaleAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 100,
        friction: 8,
      }).start();
    }
  }, [showSuccess, scaleAnim]);

  const resetForm = () => {
    setIssueType(null);
    setDescription("");
    setContactEmail("");
    setShowSuccess(false);
    scaleAnim.setValue(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validate form
    if (!issueType) {
      Alert.alert("Error", "Please select an issue type");
      return;
    }

    if (description.trim().length < 10) {
      Alert.alert("Error", "Please provide a more detailed description");
      return;
    }

    try {
      setIsSubmitting(true);

      const submissionData = {
        opportunityId: opportunity?.id,
        issueType: issueType,
        description: description.trim(),
        contactEmail: contactEmail.trim() || undefined,
      };

      // Submit the report to Parse
      await reportIssue(submissionData);

      // Show success animation
      setIsSubmitting(false);
      setShowSuccess(true);
    } catch (error) {
      console.error("Error submitting report:", error);
      
      let userFriendlyMessage = "Failed to submit report. Please try again later.";
      
      // Provide more specific error messages based on the error type
      if (error instanceof Error) {
        if (error.message.includes('network') || error.message.includes('fetch')) {
          userFriendlyMessage = "Network error. Please check your internet connection and try again.";
        } else if (error.message.includes('Parse') || error.message.includes('server')) {
          userFriendlyMessage = "Server error. Please try again in a few minutes.";
        } else if (error.message.includes('CORS')) {
          userFriendlyMessage = "Browser security error. Please try refreshing the page or using a different browser.";
        }
      }
      
      Alert.alert("Submission Failed", userFriendlyMessage);
      setIsSubmitting(false);
    }
  };

  // Safari-specific styles for better compatibility
  const isSafari = typeof navigator !== 'undefined' && 
                  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const safariStyles = isSafari ? {
    textInput: {
      WebkitAppearance: 'none',
      borderRadius: 8,
    },
    textArea: {
      WebkitAppearance: 'none',
      borderRadius: 8,
      resize: 'vertical',
    }
  } : {};

  const SuccessScreen = () => (
    <View style={styles.successContainer}>
      <Animated.View 
        style={[
          styles.successContent,
          { transform: [{ scale: scaleAnim }] }
        ]}
      >
        <View style={styles.checkmarkContainer}>
          <Ionicons name="checkmark-circle" size={64} color="#10B981" />
        </View>
        <Text style={styles.successTitle}>Report Submitted!</Text>
        <Text style={styles.successMessage}>
          Thank you for helping us improve. We'll review the issue and update the opportunity information if needed.
        </Text>
        <TouchableOpacity
          style={styles.continueButton}
          onPress={handleClose}
        >
          <Text style={styles.continueButtonText}>Continue</Text>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleClose}
      data-oid="dez__ss"
    >
      <SafeAreaView style={styles.safeArea} data-oid="pp0mh3w">
        <View style={styles.container} data-oid="cenz6sw">
          {showSuccess ? (
            <SuccessScreen />
          ) : (
            <>
              <View style={styles.header} data-oid="917b48o">
                <Text style={styles.title} data-oid="sp:ua01">
                  Report an Issue
                </Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={handleClose}
                  data-oid="p_sh3vy"
                >
                  <Ionicons
                    name="close"
                    size={24}
                    color="#4A5568"
                    data-oid="__2fmgo"
                  />
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.content} data-oid="h6lvr-a">
                {opportunity && (
                  <View
                    style={[
                      styles.opportunityInfo,
                      { borderTopColor: getTypeColor(opportunity.type) },
                    ]}
                    data-oid="efeg44."
                  >
                    <Text style={styles.opportunityTitle} data-oid="ua_bmil">
                      {opportunity.title}
                    </Text>
                    <Text style={styles.opportunityOrg} data-oid="wl2mflq">
                      {opportunity.organization}
                    </Text>
                  </View>
                )}

                <Text style={styles.sectionTitle} data-oid="2kafvhl">
                  What issue would you like to report?
                </Text>

                <View style={styles.issueOptions} data-oid="etvhpug">
                  {(
                    [
                      "Incorrect Dates",
                      "Invalid Information",
                      "Broken Link",
                      "Other",
                    ] as IssueType[]
                  ).map((type) => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.issueOption,
                        issueType === type && styles.selectedIssueOption,
                      ]}
                      onPress={() => setIssueType(type)}
                      data-oid="ikdnnf1"
                    >
                      <View style={styles.radioButton} data-oid="u9z299w">
                        {issueType === type && (
                          <View
                            style={styles.radioButtonInner}
                            data-oid="7-qeg8c"
                          />
                        )}
                      </View>
                      <Text style={styles.issueOptionText} data-oid="hjgwsf4">
                        {type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <Text style={styles.inputLabel} data-oid="3zm_ylx">
                  Describe the issue:
                </Text>
                <TextInput
                  style={[styles.textArea, safariStyles.textArea]}
                  placeholder="Please provide details about the issue you found..."
                  multiline={true}
                  numberOfLines={6}
                  textAlignVertical="top"
                  value={description}
                  onChangeText={setDescription}
                  data-oid="lapef2w"
                />

                <Text style={styles.inputLabel} data-oid="yt8lg.s">
                  Your email (optional):
                </Text>
                <TextInput
                  style={[styles.textInput, safariStyles.textInput]}
                  placeholder="email@example.com"
                  keyboardType="email-address"
                  value={contactEmail}
                  onChangeText={setContactEmail}
                  data-oid="11.v.:k"
                />

                <Text style={styles.disclaimer} data-oid="wk:b8b.">
                  We'll only use your email to follow up about this specific issue,
                  if necessary.
                </Text>

                <TouchableOpacity
                  style={[
                    styles.submitButton,
                    isSubmitting && styles.submitButtonDisabled,
                  ]}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                  data-oid="3kby431"
                >
                  {isSubmitting ? (
                    <ActivityIndicator
                      size="small"
                      color="#FFFFFF"
                      data-oid="ui.c9.q"
                    />
                  ) : (
                    <>
                      <Ionicons
                        name="paper-plane-outline"
                        size={18}
                        color="#fff"
                        data-oid="le3rn_g"
                      />

                      <Text style={styles.submitButtonText} data-oid="td1de:w">
                        Submit Report
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </ScrollView>
            </>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    width: "90%",
    maxWidth: 500,
    maxHeight: "90%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    color: "#2D3748",
    fontFamily: "Inter-Bold",
  },
  closeButton: {
    padding: 8,
    borderRadius: 16,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  opportunityInfo: {
    backgroundColor: "#F7FAFC",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderTopWidth: 4,
    borderTopColor: "#3B82F6",
  },
  opportunityTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#2D3748",
    marginBottom: 6,
    fontFamily: "Inter-Bold",
  },
  opportunityOrg: {
    fontSize: 14,
    color: "#718096",
    fontFamily: "Inter-Regular",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 16,
    fontFamily: "Inter-SemiBold",
  },
  issueOptions: {
    marginBottom: 24,
  },
  issueOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  selectedIssueOption: {
    backgroundColor: "#F7FAFC",
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#272727",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#272727",
  },
  issueOptionText: {
    fontSize: 16,
    color: "#4A5568",
    fontFamily: "Inter-Regular",
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  textArea: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 16,
    color: "#4A5568",
    minHeight: 100,
    fontFamily: "Inter-Regular",
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    fontSize: 16,
    color: "#4A5568",
    fontFamily: "Inter-Regular",
  },
  disclaimer: {
    fontSize: 12,
    color: "#718096",
    marginBottom: 20,
    fontFamily: "Inter-Regular",
  },
  submitButton: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#272727",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
    marginLeft: 8,
    fontFamily: "Inter-SemiBold",
  },
  successContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  successContent: {
    alignItems: "center",
    width: "100%",
  },
  checkmarkContainer: {
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#2D3748",
    fontFamily: "Inter-Bold",
    marginBottom: 12,
    textAlign: "center",
  },
  successMessage: {
    fontSize: 16,
    color: "#4A5568",
    fontFamily: "Inter-Regular",
    textAlign: "center",
    lineHeight: 24,
    marginBottom: 32,
  },
  continueButton: {
    backgroundColor: "#10B981",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 32,
    minWidth: 120,
  },
  continueButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    textAlign: "center",
  },
});

export default ReportIssueForm;
